import React, { useEffect, useState } from "react";
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from "material-react-table";
import { getBondMonitorColumns } from "./table/columnsConfig/bondMonitor";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { PageHeader } from "./ui";

const BondMonitor = ({ bond_monitor_data }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  // ===== STATE =====
  const [savedGlobalFilterBondMonitor, setSavedGlobalFilterBondMonitor] =
    useState(localStorage.getItem("bondMonitorTableGlobalFilter") || "");
  const [globalFilterBondMonitor, setGlobalFilterBondMonitor] = useState(
    savedGlobalFilterBondMonitor,
  );

  // ===== EFFECTS =====
  useEffect(() => {
    if (globalFilterBondMonitor !== savedGlobalFilterBondMonitor) {
      setSavedGlobalFilterBondMonitor(globalFilterBondMonitor || "");
      if (!globalFilterBondMonitor) {
        localStorage.removeItem("bondMonitorTableGlobalFilter");
      } else {
        localStorage.setItem(
          "bondMonitorTableGlobalFilter",
          globalFilterBondMonitor,
        );
      }
    }
  }, [globalFilterBondMonitor, savedGlobalFilterBondMonitor]);

  // ===== CONFIG =====
  const tableConfig = {
    columns: getBondMonitorColumns(),
    data: bond_monitor_data,
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    enableDensityToggle: false,
    virtualizerProps: { overscan: 10 },
    defaultDisplayColumn: { enableResizing: false },
    initialState: {
      density: "compact",
      sorting: [{ id: "Target_Ticker", desc: false }],
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: savedGlobalFilterBondMonitor,
      columnPinning: isDesktop
        ? { left: ["Source_Sheet", "Underlying", "Target_Ticker"] } // Pin columns on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      globalFilter: savedGlobalFilterBondMonitor,
    },
    onGlobalFilterChange: setGlobalFilterBondMonitor,
    muiTableHeadCellProps: {
      align: "right",
      sx: {
        fontWeight: 600,
        whiteSpace: "normal",
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        fontSize: {
          xs: "0.7rem", // Smaller font for mobile
          sm: "0.8rem",
        },
        letterSpacing: "0.3px",
        borderBottom: `2px solid ${theme.palette.divider}`,
        padding: {
          xs: theme.spacing(0.5, 0.25, 0.25, 0.25), // Reduced padding for mobile
          sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
        },
        verticalAlign: "bottom",
        lineHeight: "1.1",
        minHeight: {
          xs: "40px", // Taller header for mobile
          sm: "35px",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: {
          xs: theme.spacing(0.5), // Reduced padding for mobile
          sm: theme.spacing(1),
        },
        fontSize: {
          xs: "0.75rem", // Smaller font for mobile
          sm: "0.875rem",
        },
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "45px", // Taller rows for mobile readability
          sm: "40px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        backgroundColor: theme.palette.background.default,
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: theme.palette.primary.dark,
        fontWeight: 600,
        position: "sticky",
        bottom: 0,
        zIndex: 100, // Increased for visibility
        borderTop: `2px solid ${theme.palette.divider}`,
        height: "40px", // Consistent footer height
        color: theme.palette.primary.light,
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        maxWidth: "100%", // Prevent overflow
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "40px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  };

  // ===== MEMOIZED VALUES =====
  const table = useMaterialReactTable(tableConfig);

  // ===== HANDLERS =====
  // (No custom handlers in this component)

  // ===== RENDER LOGIC =====
  // (No conditional rendering logic in this component)

  // ===== RETURN =====
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        borderRadius: 0,
        boxShadow: theme.shadows[1],
      }}
    >
      <PageHeader
        sx={{ px: "20px", justifyContent: "flex-start", alignItems: "end" }}
        title={"Bond Monitor"}
        action={
          <Box
            sx={{
              display: "flex",
              alignItems: "center", // vertically center items
              justifyContent: "flex-start", // left align
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Search Field */}
            <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
              <MRT_GlobalFilterTextField
                table={table}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.divider}`,
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.primary.dark,
                    },
                    "&.Mui-focused": {
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.primary.dark,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: theme.spacing(0.75, 1.25),
                    color: theme.palette.text.primary,
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        }
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
          paddingLeft: "8px",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <MaterialReactTable
          table={table}
          muiTablePaperProps={{
            sx: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
            },
          }}
          muiTableContainerProps={{
            sx: {
              flex: "1 1 auto",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                height: "8px",
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default BondMonitor;
